import { get, isArray, reduce, isString, isObject, set } from 'lodash-es';
import {
  Connections,
  Openapi,
  RequestArgs,
  parseConnectionString,
} from './connections';
import { Resource } from './resource';
import { AxiosResponse } from 'axios';

interface GetOptions extends RequestArgs {
  asResource?: boolean;
  asResourceContainer?: boolean;
  map?: boolean;
  key?: string;
  path?: string;
}

function asResponse(value: any, options: GetOptions) {
  if (options?.path) {
    value = get(value, options.path);
  }

  if (options?.map && isString(options.key) && isArray(value)) {
    const key: string = options.key;
    value = reduce(value, (m, v) => set(m, get(v, key), v), {});
  }

  if (options?.asResource && isObject(value)) {
    return new Resource(value);
  }

  return value;
}

function getCacheKey(operation: string | { url: string }, options: GetOptions) {
  let extras = '';
  if (options?.parameters) {
    extras = reduce(options.parameters, (m, v, k) => `${m}&${v}=${k}`);
  }

  return (isString(operation) ? operation : operation.url) + extras;
}

export class Repository {
  readonly connections: Connections;
  readonly cache: Record<string, any>;

  constructor(conn: Connections) {
    this.connections = conn;
    this.cache = {};
  }

  public get(operation: string | { url: string }, options: GetOptions) {
    const cacheKey = getCacheKey(operation, options);
    const self = this;

    if (!(cacheKey in this.cache)) {
      this.cache[cacheKey] = new Promise((resolve, reject) => {
        if (isString(operation)) {
          options = options || {
            asResource: false,
            asResourceContainer: false,
          };

          const cn = parseConnectionString(operation);
          const conn = this.connections.getConnection(cn.connection) as Openapi;

          return conn
            .request(operation, options)
            .then((response) => onResponse(response))
            .catch(reject);
        }

        const conn = this.connections.default as Openapi;
        const url = `${conn.config.baseUrl}${operation.url}`;

        return this.connections
          .ajax(url, options)
          .then((response) => onResponse(response))
          .catch(reject);

        function onResponse(response: AxiosResponse) {
          if (response.status === 204) {
            return resolve(null);
          }

          return resolve(response.data);
        }
      });
    }

    return Promise.resolve(self.cache[cacheKey]).then((data: any) =>
      asResponse(data, options),
    );
  }

  public clearCache(operation: string | { url: string }, options: GetOptions) {
    const cacheKey = getCacheKey(operation, options);

    if (cacheKey in this.cache) {
      delete this.cache[cacheKey];
    }
  }

  public resetCache(prefix?: string) {
    for (let k of Object.keys(this.cache)) {
      if (prefix == null || k.startsWith(prefix)) {
        delete this.cache[k];
      }
    }
  }
}
